import {
  Button,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Switch,
  Table as ChakraTable,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { ArticleCtaSlot, ArticleCtaType, OlaggModule, PermissionEnum } from '@olagg/db-types'
import { cdn } from '@olagg/ui-kit/cloudflareCdn'
import { generatePath, useNavigate } from 'react-router-dom'
import { ROUTES } from '../../consts/routes'
import useRoles from '../../features/Roles/hooks/useRoles'
import { hasPlaceholder } from '@olagg/validation-schemas/articleCta.schemas'
import { slotOptions, targetingSummary, visibilityNote } from './constants'

const thumbnail = (cta: ArticleCtaType) => {
  const url = cta.content?.es?.imageUrl

  if (!url || hasPlaceholder(url)) return null

  return cdn(url, { w: 64, h: 64, fit: 'cover', format: 'auto' })
}

const validity = (cta: ArticleCtaType) => {
  const from = cta.startsAt ? new Date(cta.startsAt).toLocaleDateString() : 'siempre'
  const to = cta.endsAt ? new Date(cta.endsAt).toLocaleDateString() : 'siempre'

  return `${from} → ${to}`
}

const Table = ({
  articleCtas,
  deleteArticleCta,
  toggleActive,
}: {
  articleCtas: ArticleCtaType[],
  deleteArticleCta: (id: number) => Promise<void>,
  toggleActive: (cta: ArticleCtaType) => void,
}) => {
  const navigate = useNavigate()
  const { missingPermission } = useRoles()
  // Los slots que hoy compiten: la precedencia se resuelve entre los activos.
  const activeSlots = articleCtas.filter(cta => cta.active).map(cta => cta.slot) as ArticleCtaSlot[]

  const editCta = (id: number) => navigate(generatePath(ROUTES.ARTICLE_CTAS.update, { id }))

  /**
   * Duplicar: los brazos de un experimento se diferencian en uno o dos campos, y
   * volver a llenar el contenido en tres idiomas para cambiar el copy de un botón
   * es la forma más rápida de que la sección no se use.
   */
  const duplicateCta = (cta: ArticleCtaType) =>
    navigate(ROUTES.ARTICLE_CTAS.create, { state: { articleCta: cta } })

  return (
    <TableContainer overflowX={'scroll'} overflowY={'scroll'} w='80vw' h='60vh' mt='12'>
      <ChakraTable variant='striped' colorScheme='olaggPurple' fontSize='16px' position='relative'>
        <Thead>
          <Tr position='sticky' bg='gray.800' top='0' zIndex='100'>
            <Th color='white'>Activo</Th>
            <Th color='white'>Arte</Th>
            <Th color='white'>Key</Th>
            <Th color='white'>Slot</Th>
            <Th color='white'>Variante</Th>
            <Th color='white'>Targeting</Th>
            <Th color='white'>Peso</Th>
            <Th color='white'>Vigencia</Th>
            <Th color='white'>Acción</Th>
          </Tr>
        </Thead>
        <Tbody>
          {articleCtas?.map(cta => (
            <Tr key={cta.id} _hover={{ color: 'cyan' }} cursor='pointer' onClick={() => editCta(cta.id)}>
              {/* El apagado rápido se hace desde acá: sin entrar al formulario y sin deploy. */}
              <Td onClick={e => e.stopPropagation()}>
                <Switch
                  isChecked={cta.active}
                  colorScheme='purple'
                  isDisabled={missingPermission(OlaggModule.ArticleCta, PermissionEnum.EDIT)}
                  onChange={() => toggleActive(cta)}
                />
              </Td>
              <Td>
                {thumbnail(cta)
                  ? <img src={thumbnail(cta)!} width={48} height={48} alt='' style={{ objectFit: 'cover', borderRadius: '4px' }} />
                  : <Text fontSize='xs' color='gray.400'>{hasPlaceholder(cta.content?.es?.imageUrl || '') ? 'dinámica' : 'sin arte'}</Text>}
              </Td>
              <Td>{cta.key}</Td>
              <Td maxW='260px' whiteSpace='normal'>
                {slotOptions.find(option => option.value === cta.slot)?.label || cta.slot}
                {cta.active && (
                  <Text fontSize='xs' color='gray.400'>{visibilityNote(cta.slot, activeSlots)}</Text>
                )}
              </Td>
              <Td>
                {cta.variant}
                {cta.experimentKey && <Text fontSize='xs' color='gray.400'>{cta.experimentKey}</Text>}
              </Td>
              <Td maxW='260px' whiteSpace='normal'>{targetingSummary(cta.targeting)}</Td>
              <Td>{cta.weight}</Td>
              <Td>{validity(cta)}</Td>
              <Td textAlign='center'>
                <Flex gap='3'>
                  <Button
                    size={'sm'} padding='0px 8px' colorScheme='green'
                    onClick={e => { e.stopPropagation(); editCta(cta.id) }}
                    isDisabled={missingPermission(OlaggModule.ArticleCta, PermissionEnum.EDIT)}
                  >
                    Editar
                  </Button>

                  <Button
                    size={'sm'} padding='0px 8px' colorScheme='purple'
                    onClick={e => { e.stopPropagation(); duplicateCta(cta) }}
                    isDisabled={missingPermission(OlaggModule.ArticleCta, PermissionEnum.CREATE)}
                  >
                    Duplicar
                  </Button>

                  <Popover>
                    <PopoverTrigger>
                      <Button
                        size={'sm'} padding='0px 8px' ml={0} colorScheme='red'
                        onClick={e => e.stopPropagation()}
                        isDisabled={missingPermission(OlaggModule.ArticleCta, PermissionEnum.DELETE)}
                      >
                        Eliminar
                      </Button>
                    </PopoverTrigger>
                    <Portal>
                      <PopoverContent bgColor='gray.800'>
                        <PopoverArrow bgColor='gray.800' />
                        <PopoverCloseButton />
                        <PopoverBody>
                          <Text mb='3'>¿Eliminar el CTA {cta.key}?</Text>
                          <Button
                            size='sm' colorScheme='red'
                            onClick={e => { e.stopPropagation(); deleteArticleCta(cta.id) }}
                          >
                            Confirmar
                          </Button>
                        </PopoverBody>
                      </PopoverContent>
                    </Portal>
                  </Popover>
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </ChakraTable>
    </TableContainer>
  )
}

export default Table
